import { Component, OnInit, Inject, PLATFORM_ID, ElementRef, ViewChild } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { LanguageService } from '../../services/language.service';

declare const google: any;
declare const FB: any;

interface AuthResponse {
  token?: string;
  email?: string;
  fullName?: string;
  phone?: string;
  address?: string;
  role?: string;
  error?: string;
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './login.html',
  styleUrls: ['./login.scss']
})
export class LoginComponent implements OnInit {

  email = '';
  password = '';

  loading = false;
  showPassword = false;
  socialLoading = false;

  // ✅ API URL
  apiUrl = 'https://superbangladesh-api-1.onrender.com';

  // =========================================================
  // 🔵 GOOGLE — Google Cloud Console > APIs & Services > Credentials
  // > Create Credentials > OAuth client ID > Web application থেকে
  // পাওয়া Client ID। এই Client ID গোপন কিছু না (frontend-এ থাকা
  // নিরাপদ), কিন্তু backend-এর GOOGLE_CLIENT_ID env-এ ঠিক এই একই
  // Client ID বসাতে হবে — নাহলে backend token verify করতে পারবে না।
  // =========================================================
  private readonly googleClientId = '15746763097-ncuk0ht5rl85e85v3n08201ru68hamkr.apps.googleusercontent.com';

  // =========================================================
  // 🔷 FACEBOOK — developers.facebook.com > My Apps > (আপনার App)
  // > Settings > Basic থেকে App ID। App Secret এখানে কখনো বসাবেন
  // না — সেটা শুধু backend-এ (FACEBOOK_APP_SECRET env) থাকবে।
  // =========================================================
  private readonly facebookAppId = '1567388351787063';

  private readonly isBrowser: boolean;

  @ViewChild('googleBtn') googleBtnRef!: ElementRef<HTMLDivElement>;

  constructor(
    private http: HttpClient,
    private router: Router,
    public languageService: LanguageService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /* =========================
     TRANSLATE
  ========================= */

  t(key: string): string {
    return this.languageService.translate(key);
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  /* =========================
     LOAD GOOGLE + FACEBOOK SDK
     (শুধু browser-এ — SSR-এ window/document নেই)
  ========================= */

  ngOnInit(): void {

    if (!this.isBrowser) {
      return;
    }

    this.loadScript('https://accounts.google.com/gsi/client')
      .then(() => this.initGoogle())
      .catch(() => console.warn('Google SDK লোড করা যায়নি'));

    // 🔷 Facebook Login আপাতত বন্ধ — App Review/Business Verification
    // শেষ না হওয়া পর্যন্ত সাধারণ ইউজার এটা দিয়ে লগইন করতে পারবে না,
    // তাই SDK-ই লোড করা হচ্ছে না। Verification শেষ হলে নিচের লাইনটা
    // আবার আনকমেন্ট করে দিলেই যথেষ্ট — বাকি কোড (facebookLogin(),
    // initFacebook()) অপরিবর্তিত রাখা আছে।
    //
    // this.loadScript('https://connect.facebook.net/en_US/sdk.js')
    //   .then(() => this.initFacebook())
    //   .catch(() => console.warn('Facebook SDK লোড করা যায়নি'));
  }

  private loadScript(src: string): Promise<void> {

    return new Promise((resolve, reject) => {

      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject();

      document.head.appendChild(script);
    });
  }

  /* =========================
     GOOGLE LOGIN
  ========================= */

  private initGoogle(): void {

    if (typeof google === 'undefined' || !google?.accounts?.id) {
      return;
    }

    google.accounts.id.initialize({
      client_id: this.googleClientId,
      callback: (response: { credential?: string }) => this.handleGoogleResponse(response),

      // ⚠️ FedCM: 2024 থেকে Chrome/Android third-party cookie বন্ধ
      // করে দিচ্ছে, আর তার বদলে ব্রাউজারের নিজস্ব FedCM (Federated
      // Credential Management) API ব্যবহার করতে হয় — এটা ছাড়া মোবাইলে
      // "device-এ যে Google account দিয়ে আগে থেকে লগইন করা আছে" সেটা
      // দেখানো/one-click continue করা মাঝে মাঝে কাজ নাও করতে পারে বা
      // চুপচাপ ফেইল করতে পারে। এটা চালু রাখলে Google নিজেই ব্রাউজারে
      // sign-in থাকা account(গুলো) চেনে আর "Continue as [নাম]" দেখায়।
      use_fedcm_for_prompt: true,
      auto_select: false,
      itp_support: true
    });

    if (this.googleBtnRef?.nativeElement) {

      google.accounts.id.renderButton(this.googleBtnRef.nativeElement, {
        theme: 'outline',
        size: 'large',
        width: 280,
        text: 'continue_with'
      });
    }

    // One Tap: renderButton-এর বাটনের পাশাপাশি, ডিভাইসে আগে থেকে সাইন-ইন
    // করা Google account থাকলে ছোট একটা "Continue as [নাম]" prompt
    // পেজ লোড হওয়ার সাথে সাথেই কোণায় দেখায় — কোনো manual email/password
    // ছাড়াই এক ক্লিকে লগইন। কোনো account সাইন-ইন করা না থাকলে বা
    // ইউজার আগে এটা বন্ধ (dismiss) করে থাকলে চুপচাপ কিছুই দেখাবে না,
    // তখনও renderButton-এর বাটনটা কাজ করবে যেটাতেও একই account picker
    // আসবে।
    try {
      google.accounts.id.prompt();
    } catch {
      // one-tap সাপোর্ট না থাকলে চুপচাপ ignore — renderButton বাটনটা
      // থেকেই যায়, সেটা দিয়েও একই flow কাজ করবে
    }
  }

  private handleGoogleResponse(response: { credential?: string }): void {

    if (!response?.credential) {
      alert(this.t('socialLoginFailed'));
      return;
    }

    this.socialLoading = true;

    this.http.post<AuthResponse>(
      `${this.apiUrl}/api/auth/google`,
      { idToken: response.credential }
    ).subscribe({
      next: (res) => this.handleSocialSuccess(res),
      error: (err) => this.handleSocialError(err)
    });
  }

  /* =========================
     FACEBOOK LOGIN
  ========================= */

  private initFacebook(): void {

    if (typeof FB === 'undefined') {
      return;
    }

    FB.init({
      appId: this.facebookAppId,
      cookie: true,
      xfbml: false,
      version: 'v19.0'
    });
  }

  facebookLogin(): void {

    if (typeof FB === 'undefined') {
      alert(this.t('socialLoginFailed'));
      return;
    }

    FB.login((response: { authResponse?: { accessToken: string } }) => {

      if (!response?.authResponse?.accessToken) {
        alert(this.t('socialLoginFailed'));
        return;
      }

      this.socialLoading = true;

      this.http.post<AuthResponse>(
        `${this.apiUrl}/api/auth/facebook`,
        { accessToken: response.authResponse.accessToken }
      ).subscribe({
        next: (res) => this.handleSocialSuccess(res),
        error: (err) => this.handleSocialError(err)
      });

    }, { scope: 'public_profile,email' });
  }

  /* =========================
     SHARED: SOCIAL LOGIN RESULT HANDLING
  ========================= */

  private handleSocialSuccess(res: AuthResponse): void {

    this.socialLoading = false;

    if (!res || res.error) {
      alert(res?.error || this.t('socialLoginFailed'));
      return;
    }

    this.saveSessionAndRedirect(res);
  }

  private handleSocialError(err: any): void {

    this.socialLoading = false;

    alert(
      err?.error?.error ||
      err?.error?.message ||
      this.t('socialLoginFailed')
    );
  }

  private saveSessionAndRedirect(res: AuthResponse): void {

    localStorage.setItem('token', res.token || '');
    localStorage.setItem('role', res.role || '');
    localStorage.setItem('email', res.email || '');

    if (res.role === 'ADMIN') {
      window.location.href = '/admin/orders';
    } else {
      window.location.href = '/';
    }
  }

  /* =========================
     EMAIL + PASSWORD LOGIN
  ========================= */

  submit(): void {

    if (!this.email || !this.password) {
      alert(this.t('enterEmailPassword'));
      return;
    }

    this.loading = true;

    // 🔥 WAKE BACKEND FIRST (Render free-tier cold start)
    this.http.get(`${this.apiUrl}/api/settings`).subscribe({

      next: () => this.performLogin(),

      error: () => {
        this.loading = false;
        alert(this.t('serverWakingUp'));
      }
    });
  }

  private performLogin(): void {

    this.http.post<AuthResponse>(
      `${this.apiUrl}/api/auth/login`,
      { email: this.email, password: this.password }
    ).subscribe({

      next: (res) => {

        this.loading = false;

        if (!res) {
          alert(this.t('noResponseFromServer'));
          return;
        }

        if (res.error) {
          this.showLoginError(res.error);
          return;
        }

        this.saveSessionAndRedirect(res);
      },

      error: (err) => {
        this.loading = false;
        alert(
          err?.error?.message ||
          err?.message ||
          this.t('loginFailed')
        );
      }
    });
  }

  private showLoginError(error: string): void {

    if (error === 'User not found') {
      alert(this.t('noAccountFoundRegisterFirst'));
    } else if (error === 'Wrong password') {
      alert(this.t('incorrectPassword'));
    } else {
      alert(error);
    }
  }
}
